import { Box, Portal, Typography } from "@mui/material";
import { useEffect } from "react";
import type { TableGiphyPlayedData } from "../sockets-client";

const DISMISS_MS = 2000;

type Props = {
  queue: TableGiphyPlayedData[];
  onDismissHead: () => void;
};

/** Room-wide GIF toast; shows head of queue for at most 2 seconds. */
export function TableGiphyOverlay({ queue, onDismissHead }: Props) {
  const head = queue[0];

  // Timer keyed to visible item only (queue[0].eventId), not full queue reference.
  useEffect(() => {
    if (!head) return;
    const t = window.setTimeout(onDismissHead, DISMISS_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [head?.eventId, onDismissHead]);

  if (!head) return null;

  const overlay = (
    <Box
      sx={{
        position: "fixed",
        left: "50%",
        bottom: "max(20px, calc(env(safe-area-inset-bottom, 0px) + 12px))",
        transform: "translateX(-50%)",
        zIndex: 10000,
        maxWidth: "min(92vw, 360px)",
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: "rgba(18,18,28,0.95)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: 6,
        pointerEvents: "none",
        textAlign: "center",
      }}
    >
      <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.85)", mb: 0.5 }}>
        {head.fromPlayerName}
      </Typography>
      <Box
        component="img"
        src={head.playUrl}
        alt=""
        sx={{
          display: "block",
          maxHeight: 200,
          width: "100%",
          objectFit: "contain",
          borderRadius: 1,
        }}
      />
    </Box>
  );

  return typeof document !== "undefined" ? <Portal container={document.body}>{overlay}</Portal> : overlay;
}
